// Two-step sign-in (HARDEN-mfa; Better Auth's twoFactor plugin, api/client.ts AuthAdapter):
// - `EnrolFlow`: the password, then the authenticator QR code (`QrCode`) with its key for manual
//   entry and the ten recovery codes, shown once with Copy and Download (.txt), then a 6-digit
//   code from the app turns it on. Used by Account & security and by the forced screen;
// - `TwoStepSection`: Account & security's "Two-step sign-in" group — off: "Set up" (a dialog
//   with the flow); on: a badge, "New recovery codes" and "Turn off" (each takes the password).
//   An email-link account sets a password first (the Password group above);
// - `ForcedEnrolment`: the full-screen enrolment the shell shows instead of the app when
//   `/v1/me` `mfa` says the user must use two-step sign-in and has not set it up — an agency
//   operator always, an owner or admin where the company requires it (`MfaGate`, routes/_authed
//   and c.$slug). Account & security stays reachable; sign-out is on the screen.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Notice,
  QrCode,
  SettingsGroup,
  SettingsRow,
  Spinner,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CopyIcon, DownloadIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { MeResponse } from "../../api/types";
import { useSignOut } from "../../shell/app-shell";
import { AuthFrame } from "../auth/auth-frame";
import { saveBlob } from "../finance/finance-parts";

/** The same key as Account & security's Password group (account.tsx `accountKeys.password`). */
const HAS_PASSWORD = ["account", "has-password"] as const;

/** Why the app is held back: an agency operator always, or this company's owners and admins. */
export type MfaReason = "operator" | "company";

/** Whether `me` must set up two-step sign-in before using the app (in `companyId`, if given). */
export function mfaHold(me: MeResponse, companyId: string | null): MfaReason | null {
  if (me.mfa.enrolled) return null;
  if (me.mfa.operator_required) return "operator";
  if (companyId && me.mfa.required_company_ids.includes(companyId)) return "company";
  return null;
}

/** Required anywhere (an operator, or an owner or admin of a company that requires it). */
const requiredAnywhere = (me: MeResponse) =>
  me.mfa.operator_required || me.mfa.required_company_ids.length > 0;

/** The base32 key inside an `otpauth://` link, in groups of four for typing it in. */
export function manualKeyOf(totpURI: string): string {
  let secret = "";
  try {
    secret = new URL(totpURI).searchParams.get("secret") ?? "";
  } catch {
    secret = /[?&]secret=([^&]+)/.exec(totpURI)?.[1] ?? "";
  }
  return (secret.match(/.{1,4}/g) ?? []).join(" ");
}

const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/** Recovery codes, shown once: a list, Copy and Download (.txt). */
function RecoveryCodes({ codes, email }: { codes: string[]; email: string }) {
  const text = `CompanyOS recovery codes for ${email}\nEach code works once.\n\n${codes.join("\n")}\n`;
  return (
    <div className="flex flex-col gap-2" data-slot="recovery-codes">
      <ul
        aria-label="Recovery codes"
        className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-subtle bg-sunken px-3 py-2 font-mono text-[13px] tabular-nums"
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() =>
            void navigator.clipboard?.writeText(codes.join("\n")).then(
              () => toast.add({ title: "Recovery codes copied", type: "success" }),
              () => toast.add({ title: "Could not copy: select the codes instead", type: "error" }),
            )
          }
        >
          <CopyIcon data-icon="inline-start" />
          Copy
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() =>
            saveBlob(new Blob([text], { type: "text/plain" }), "companyos-recovery-codes.txt")
          }
        >
          <DownloadIcon data-icon="inline-start" />
          Download
        </Button>
      </div>
    </div>
  );
}

/** One password field and its submit button (enable, new codes, turn off). */
function PasswordForm({
  id,
  submitLabel,
  pendingLabel,
  pending,
  error,
  destructive = false,
  onSubmit,
  children,
}: {
  id: string;
  submitLabel: string;
  pendingLabel: string;
  pending: boolean;
  error: string | null;
  destructive?: boolean;
  onSubmit: (password: string) => void;
  children?: ReactNode;
}) {
  const [password, setPassword] = useState("");
  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (password) onSubmit(password);
      }}
    >
      {children}
      <Field>
        <FieldLabel htmlFor={id}>Your password</FieldLabel>
        <Input
          id={id}
          type="password"
          autoComplete="current-password"
          className="h-10"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <Button
        type="submit"
        className="self-end max-sm:self-stretch"
        variant={destructive ? "destructive" : "default"}
        disabled={pending || !password}
      >
        {pending ? <Spinner /> : null}
        {pending ? pendingLabel : submitLabel}
      </Button>
    </form>
  );
}

/**
 * Enrolment: password → QR code, key and recovery codes → a code from the app. `onDone` runs
 * once it is on (`/v1/me` is reread, so a forced screen lifts by itself).
 */
export function EnrolFlow({
  api,
  email,
  idPrefix,
  onDone,
}: {
  api: Api;
  email: string;
  idPrefix: string;
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const has = useQuery({ queryKey: HAS_PASSWORD, queryFn: () => api.auth.hasPassword() });
  const [setup, setSetup] = useState<{ totpURI: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState("");
  const enable = useMutation({
    mutationFn: (password: string) => api.auth.enableTwoFactor(password),
    onSuccess: (r) => setSetup(r),
  });
  const confirm = useMutation({
    mutationFn: () => api.auth.confirmTwoFactor(code.replace(/\s/g, "")),
    onSuccess: async () => {
      toast.add({ title: "Two-step sign-in is on", type: "success" });
      await qc.invalidateQueries({ queryKey: ["me"] });
      onDone?.();
    },
  });

  if (has.isPending) return <Spinner />;
  if (has.data === false)
    return (
      <Notice tone="warning" title="Set a password first" role="status">
        Two-step sign-in asks for your password when you turn it on or off, and you sign in with a
        password and a code. Set one in{" "}
        <Link to="/account" hash="password" className="underline">
          Account &amp; security › Password
        </Link>
        , then come back here.
      </Notice>
    );

  if (!setup)
    return (
      <PasswordForm
        id={`${idPrefix}-password`}
        submitLabel="Continue"
        pendingLabel="Checking…"
        pending={enable.isPending}
        error={enable.isError ? message(enable.error, "Not set up.") : null}
        onSubmit={(p) => enable.mutate(p)}
      >
        <p className="text-sm text-muted-foreground">
          You will need an authenticator app on your phone (Google Authenticator, 1Password, Authy,
          Microsoft Authenticator…). Confirm your password to start.
        </p>
      </PasswordForm>
    );

  const typed = code.replace(/\s/g, "");
  return (
    <form
      noValidate
      className="flex flex-col gap-5"
      data-slot="mfa-enrol"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (/^\d{6}$/.test(typed)) confirm.mutate();
      }}
    >
      <section className="flex flex-col gap-2" aria-labelledby={`${idPrefix}-scan`}>
        <h3 id={`${idPrefix}-scan`} className="text-sm font-medium">
          1. Scan this code with your authenticator app
        </h3>
        <QrCode value={setup.totpURI} label="QR code for your authenticator app" />
        <p className="text-xs text-muted-foreground">Can't scan it? Enter this key instead:</p>
        <code
          data-slot="mfa-key"
          className="w-fit max-w-full rounded-sm bg-sunken px-2 py-1 font-mono text-[13px] break-all"
        >
          {manualKeyOf(setup.totpURI)}
        </code>
      </section>
      <section className="flex flex-col gap-2" aria-labelledby={`${idPrefix}-codes`}>
        <h3 id={`${idPrefix}-codes`} className="text-sm font-medium">
          2. Save your recovery codes
        </h3>
        <p className="text-xs text-muted-foreground">
          Each one signs you in once if you lose your phone. They are shown only now.
        </p>
        <RecoveryCodes codes={setup.backupCodes} email={email} />
      </section>
      <section className="flex flex-col gap-2">
        <Field>
          <FieldLabel htmlFor={`${idPrefix}-code`}>
            3. Enter the 6-digit code from the app
          </FieldLabel>
          <Input
            id={`${idPrefix}-code`}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            className="h-10 w-40 font-mono tracking-widest tabular-nums max-sm:w-full"
            value={code}
            aria-invalid={confirm.isError || undefined}
            onChange={(e) => setCode(e.target.value)}
          />
          <FieldDescription>Two-step sign-in is on once the code checks out.</FieldDescription>
        </Field>
        {confirm.isError ? (
          <Notice tone="danger" role="alert">
            {message(confirm.error, "The code was not accepted.")}
          </Notice>
        ) : null}
        <Button
          type="submit"
          className="self-end max-sm:self-stretch"
          disabled={confirm.isPending || !/^\d{6}$/.test(typed)}
        >
          {confirm.isPending ? <Spinner /> : null}
          Turn on two-step sign-in
        </Button>
      </section>
    </form>
  );
}

/** Account & security › Two-step sign-in. */
export function TwoStepSection({ api, me }: { api: Api; me: MeResponse }) {
  const qc = useQueryClient();
  const has = useQuery({ queryKey: HAS_PASSWORD, queryFn: () => api.auth.hasPassword() });
  const [dialog, setDialog] = useState<"setup" | "codes" | "off" | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const regenerate = useMutation({
    mutationFn: (password: string) => api.auth.regenerateRecoveryCodes(password),
    onSuccess: (r) => setCodes(r),
  });
  const disable = useMutation({
    mutationFn: (password: string) => api.auth.disableTwoFactor(password),
    onSuccess: async () => {
      setDialog(null);
      toast.add({ title: "Two-step sign-in is off", type: "success" });
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const open = (d: typeof dialog) => {
    regenerate.reset();
    disable.reset();
    setCodes(null);
    setDialog(d);
  };
  const on = me.mfa.enrolled;
  const required = requiredAnywhere(me);
  const noPassword = has.data === false;

  return (
    <SettingsGroup id="two-step" title="Two-step sign-in">
      <SettingsRow
        label="Authenticator app"
        hint={
          on
            ? "When you sign in on a new device, you enter a code from your app after your password."
            : "A code from an app on your phone at each sign-in, on top of your password."
        }
      >
        <Badge variant={on ? "success" : "neutral"}>{on ? "On" : "Off"}</Badge>
        {on ? null : (
          <Button size="sm" disabled={noPassword || has.isPending} onClick={() => open("setup")}>
            Set up
          </Button>
        )}
      </SettingsRow>
      {!on && required ? (
        <div className="border-t border-subtle px-4 py-2.5">
          <Notice tone="warning" role="status">
            {me.mfa.operator_required
              ? "Your agency requires two-step sign-in: set it up to keep using CompanyOS."
              : "A company you manage requires two-step sign-in of its owners and admins."}
          </Notice>
        </div>
      ) : null}
      {!on && noPassword ? (
        <SettingsRow
          label="Set a password first"
          hint="You sign in with email links. Two-step sign-in needs a password: set one in Password above."
        />
      ) : null}
      {on ? (
        <>
          <SettingsRow
            label="Recovery codes"
            hint="For when you don't have your phone. New codes replace the old ones."
          >
            <Button size="sm" variant="secondary" onClick={() => open("codes")}>
              New recovery codes
            </Button>
          </SettingsRow>
          <SettingsRow label="Turn off two-step sign-in" hint="Your password alone signs you in.">
            <Button size="sm" variant="ghost" onClick={() => open("off")}>
              Turn off
            </Button>
          </SettingsRow>
        </>
      ) : null}

      <Dialog open={dialog !== null} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          {dialog === "setup" ? (
            <>
              <DialogHeader>
                <DialogTitle>Set up two-step sign-in</DialogTitle>
                <DialogDescription>
                  After your password, you will enter a code from your authenticator app.
                </DialogDescription>
              </DialogHeader>
              <EnrolFlow
                api={api}
                email={me.user.email}
                idPrefix="mfa-setup"
                onDone={() => setDialog(null)}
              />
            </>
          ) : dialog === "codes" ? (
            <>
              <DialogHeader>
                <DialogTitle>New recovery codes</DialogTitle>
                <DialogDescription>
                  {codes
                    ? "Save these now: they are shown only once. The old codes no longer work."
                    : "Your current recovery codes stop working once you make new ones."}
                </DialogDescription>
              </DialogHeader>
              {codes ? (
                <>
                  <RecoveryCodes codes={codes} email={me.user.email} />
                  <DialogFooter>
                    <Button onClick={() => setDialog(null)}>Done</Button>
                  </DialogFooter>
                </>
              ) : (
                <PasswordForm
                  id="mfa-codes-password"
                  submitLabel="Make new codes"
                  pendingLabel="Making…"
                  pending={regenerate.isPending}
                  error={regenerate.isError ? message(regenerate.error, "No new codes.") : null}
                  onSubmit={(p) => regenerate.mutate(p)}
                />
              )}
            </>
          ) : dialog === "off" ? (
            <>
              <DialogHeader>
                <DialogTitle>Turn off two-step sign-in?</DialogTitle>
                <DialogDescription>
                  Your password alone will sign you in, and your recovery codes stop working.
                </DialogDescription>
              </DialogHeader>
              <PasswordForm
                id="mfa-off-password"
                submitLabel="Turn off"
                pendingLabel="Turning off…"
                destructive
                pending={disable.isPending}
                error={disable.isError ? message(disable.error, "Still on.") : null}
                onSubmit={(p) => disable.mutate(p)}
              >
                {required ? (
                  <Notice tone="warning" role="status">
                    {me.mfa.operator_required
                      ? "Your agency requires two-step sign-in: you will be asked to set it up again before you can go on."
                      : "A company you manage requires it: you will be asked to set it up again before you can open it."}
                  </Notice>
                ) : null}
              </PasswordForm>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </SettingsGroup>
  );
}

/**
 * The full-screen enrolment in front of the app (operators always; owners and admins where the
 * company requires it). Setting it up lifts it: `/v1/me` is reread.
 */
export function ForcedEnrolment({
  api,
  me,
  reason,
  companyName,
}: {
  api: Api;
  me: MeResponse;
  reason: MfaReason;
  companyName?: string;
}) {
  const signOut = useSignOut(api);
  return (
    <AuthFrame
      title="Set up two-step sign-in"
      description={
        reason === "operator"
          ? "Your agency requires two-step sign-in of every operator. Set it up to continue."
          : `${companyName ?? "This company"} requires two-step sign-in of its owners and admins. Set it up to continue.`
      }
    >
      <div data-slot="mfa-forced" className="flex flex-col gap-5">
        <EnrolFlow api={api} email={me.user.email} idPrefix="mfa-forced" />
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-subtle pt-4">
          <Link to="/account" className="text-sm text-secondary underline">
            Account &amp; security
          </Link>
          <Button variant="ghost" size="sm" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </div>
    </AuthFrame>
  );
}
